import { z } from 'zod';

/**
 * Customers — the legal entities a vendor sells to.
 *
 * Organization-scoped, not product-scoped: one customer buys several of a
 * vendor's products, and filing them under a product would create one copy of
 * the same company per product.
 */
export const customerSchema = z
  .object({
    object: z.literal('customer'),
    id: z.string(),
    organization_id: z.string(),
    name: z.string(),
    /**
     * A contact address, and nothing more.
     *
     * Never an authentication factor and never used for marketing by the
     * platform. Nullable because a vendor importing from their own CRM may not
     * have one yet, and demanding it would produce invented `noreply@` addresses
     * that look real to everything downstream.
     */
    contact_email: z.string().nullable(),
    /** The vendor's own key — their CRM or billing id. Unique per organization. */
    external_reference: z.string().nullable(),
    status: z.enum(['ACTIVE', 'ARCHIVED']),
    version: z.int().min(1),
    created_at: z.iso.datetime(),
    updated_at: z.iso.datetime(),
    archived_at: z.iso.datetime().nullable(),
  })
  .meta({ id: 'Customer' });

export type CustomerResource = z.infer<typeof customerSchema>;
export type CustomerResponse = CustomerResource;

const contactEmail = z
  .string()
  .max(320)
  .regex(/^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/, 'That does not look like an email address.');

export const createCustomerRequestSchema = z
  .object({
    name: z.string().min(1).max(200),
    contact_email: contactEmail.nullable().optional(),
    /**
     * Optional, and idempotency-shaped.
     *
     * When a vendor supplies their own key, a repeated import resolves the
     * customer they already created instead of making a second one. Unique per
     * organization — two vendors may both use `ACME-001`.
     */
    external_reference: z.string().min(1).max(128).nullable().optional(),
  })
  .meta({ id: 'CreateCustomerRequest' });

export const updateCustomerRequestSchema = z
  .object({
    expected_version: z.int().min(1),
    name: z.string().min(1).max(200).optional(),
    contact_email: contactEmail.nullable().optional(),
    external_reference: z.string().min(1).max(128).nullable().optional(),
  })
  .meta({ id: 'UpdateCustomerRequest' });

export const archiveCustomerRequestSchema = z
  .object({ expected_version: z.int().min(1) })
  .meta({ id: 'ArchiveCustomerRequest' });
